// ================================================================
// mountReact — Bruecke zwischen Vanilla-JS und React ("Island"-Muster)
// Erlaubt es, aus dem bestehenden Vanilla-Code eine React-Komponente
// in ein DOM-Element zu mounten und sauber wieder zu entfernen.
// Kern der inkrementellen Migration: neue Features als React-Inseln.
// ================================================================
import { createElement, type ComponentType } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { ErrorBoundary } from './ErrorBoundary';
import './ErrorBoundary.css';

// Merkt sich pro Container die zugehoerige React-Root, damit wir
// spaeter sauber unmounten koennen (kein Memory-Leak).
// Bewusst eine Map (nicht WeakMap): sie muss durchlaufbar sein, damit
// pruneDetachedRoots() Inseln aufraeumen kann, deren Container nicht mehr im
// Dokument haengt. Eintraege werden dort geloescht — kein Wachstum.
const roots = new Map<Element, Root>();

/**
 * Raeumt Inseln auf, deren Container aus dem DOM verschwunden ist.
 *
 * Die Vanilla-Shell (CompanyDashboard) zeichnet per `innerHTML` neu. Das
 * ersetzt den Container einer Insel, OHNE dass jemand unmountet — die alte
 * React-Root lebt unsichtbar weiter, mitsamt ihrer Effekte. Handler auf
 * globalen Bussen (core/uiCommands) bleiben so registriert, und ein einziger
 * Assistenten-Befehl wird von mehreren Geister-Instanzen ausgefuehrt: im
 * Offerten-Flow legte das den Kunden und den Entwurf DOPPELT an.
 */
function pruneDetachedRoots(): void {
  for (const [el, root] of roots) {
    if (document.contains(el)) continue;
    roots.delete(el);
    // Nicht synchron: unmount() waehrend eines laufenden React-Renders wirft
    // eine Warnung. Der Container ist ohnehin schon weg — ein Tick spaeter
    // aufzuraeumen aendert fuer den Nutzer nichts.
    queueMicrotask(() => { try { root.unmount(); } catch { /* schon weg */ } });
  }
}

/**
 * Mountet eine React-Komponente in ein DOM-Element.
 * @param container Ziel-Element (z.B. ein <div id="react-root">)
 * @param Component Die React-Komponente
 * @param props    Props fuer die Komponente
 */
export function mountReact<P extends object>(
  container: Element,
  Component: ComponentType<P>,
  props: P,
): void {
  // Erst aufraeumen, dann mounten: der neue Container ist bereits im Baum,
  // die vorigen Inseln derselben Shell sind es nicht mehr.
  if (!container.isConnected) return;
  pruneDetachedRoots();
  let root = roots.get(container);
  if (!root) {
    root = createRoot(container);
    roots.set(container, root);
  }
  // JEDE Insel bekommt das Auffangnetz — hier und nicht in den einzelnen
  // Komponenten: ein Absturz reisst sonst den ganzen Baum weg und der Nutzer
  // sieht eine weisse Seite (genau so geschehen, als der Assistent die Ansicht
  // wechselte). Wer eine neue Insel baut, muss daran nicht denken.
  root.render(createElement(ErrorBoundary, { scope: Component.displayName || Component.name },
    createElement(Component, props)));
}

/**
 * Entfernt eine zuvor gemountete React-Komponente sauber.
 * @param container Das Element, in das gemountet wurde
 */
export function unmountReact(container: Element, deferred = false): void {
  const root = roots.get(container);
  if (root) {
    roots.delete(container);
    if (deferred) queueMicrotask(() => root.unmount());
    else root.unmount();
  }
}

/** Dispose islands before the owning vanilla shell replaces its descendants. */
export function unmountReactWithin(container: Element, deferred = false): void {
  for (const element of [...roots.keys()]) {
    if (element === container || container.contains(element)) unmountReact(element, deferred);
  }
}
